import { Base64 } from 'js-base64';
import dayjs from 'dayjs';

export const DEMO_SERIAL = '202104281656';
export const DEMO_TOKEN = 'demo';
export const AGENT_EMAIL = 'agent@demo.local';

export function encodeGraphqlId(type, id) {
  return Base64.encode(`${type}:${id}`);
}

function today() {
  return dayjs().format('YYYY-MM-DD');
}

function addPeriod(start, periodType) {
  if (periodType === 'YEAR') {
    return dayjs(start).add(1, 'year').format('YYYY-MM-DD');
  }
  return dayjs(start).add(1, 'month').format('YYYY-MM-DD');
}

function createProducts() {
  return [
    {
      dbId: 1,
      id: encodeGraphqlId('ProductNode', 1),
      name: 'Monthly Plan: HK$120 / Month',
      description: '<p>Unlimited karaoke for one month.</p>',
      periodType: 'MONTH',
      price: '120',
      distributorTrial: false,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
    {
      dbId: 2,
      id: encodeGraphqlId('ProductNode', 2),
      name: 'Yearly Plan: HK$1,200 / Year',
      description: '<p>Unlimited karaoke for one year.</p>',
      periodType: 'YEAR',
      price: '1200',
      distributorTrial: false,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
    {
      dbId: 3,
      id: encodeGraphqlId('ProductNode', 3),
      name: 'Distributor Trial',
      description: '<p>Complimentary trial for distributors.</p>',
      periodType: 'MONTH',
      price: '0',
      distributorTrial: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z',
    },
  ];
}

function createUser({ email, displayName, role }) {
  return {
    dbId: 1,
    id: encodeGraphqlId('UserNode', 1),
    email,
    displayName,
    role,
  };
}

function createSubscriptionRecord({ dbId, product, serialNumber, status = 'ACTIVE' }) {
  const startDate = today();
  return {
    dbId,
    productId: product.dbId,
    serialNumber,
    status,
    startDate,
    endDate: addPeriod(startDate, product.periodType),
  };
}

export function publicProduct(product) {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    periodType: product.periodType,
    createdAt: product.createdAt,
    updatedAt: product.updatedAt,
    price: product.price,
    distributorTrial: product.distributorTrial,
  };
}

export function publicSubscription(subscription, products) {
  const product = products.find((item) => item.dbId === subscription.productId);
  return {
    id: encodeGraphqlId('SubscriptionNode', subscription.dbId),
    startDate: subscription.startDate,
    endDate: subscription.endDate,
    status: subscription.status,
    device: { serialNumber: subscription.serialNumber },
    product: publicProduct(product),
    eligibleForUpgrade: product.periodType === 'MONTH' && !product.distributorTrial,
    eligibleForDowngrade: product.periodType === 'YEAR',
  };
}

function createState(scenario) {
  const products = createProducts();
  const monthly = products[0];
  const subscriptions = [];
  let nextSubscriptionId = 1;
  let nextOrderId = 1;
  let nextReceiptId = 1;
  let nextSession = 1;
  const orders = [];

  if (scenario === 'active' || scenario === 'duplicate') {
    subscriptions.push(createSubscriptionRecord({
      dbId: nextSubscriptionId++,
      product: monthly,
      serialNumber: DEMO_SERIAL,
    }));
  }

  if (scenario === 'duplicate') {
    subscriptions.push(createSubscriptionRecord({
      dbId: nextSubscriptionId++,
      product: monthly,
      serialNumber: DEMO_SERIAL,
    }));
  }

  return {
    scenario,
    user: null,
    products,
    subscriptions,
    orders,
    nextSubscriptionId,
    nextOrderId,
    nextReceiptId,
    nextSession,
  };
}

const STORAGE_KEY = 'kex-demo-store';

function loadState() {
  if (typeof sessionStorage === 'undefined') {
    return createState('empty');
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : createState('empty');
  } catch (error) {
    return createState('empty');
  }
}

function persistStore() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export { persistStore };

let state = loadState();

export function resetStore(scenario = 'empty') {
  state = createState(scenario);
  persistStore();
  return state;
}

export function getStore() {
  return state;
}

export function setUserFromEmail(email, displayName) {
  const role = email.toLowerCase() === AGENT_EMAIL ? 'AGENT' : 'CUSTOMER';
  state.user = createUser({
    email,
    displayName: displayName || (role === 'AGENT' ? 'Demo Agent' : email.split('@')[0]),
    role,
  });
  return state.user;
}

export function catalogProducts() {
  return state.products.filter((product) => !product.distributorTrial);
}

export function findProductByDbId(productId) {
  const numericId = Number(productId);
  return state.products.find((product) => product.dbId === numericId);
}

export function findSubscriptionByDbId(subscriptionId) {
  const numericId = Number(subscriptionId);
  return state.subscriptions.find((subscription) => subscription.dbId === numericId);
}

export function addPaidOrder({ product, serialNumber, previousSubscription } = {}) {
  const startDate = today();
  const subscription = createSubscriptionRecord({
    dbId: state.nextSubscriptionId++,
    product,
    serialNumber,
  });

  if (previousSubscription) {
    previousSubscription.status = product.periodType === 'YEAR' ? 'UPGRADED' : 'DOWNGRADED';
  }

  state.subscriptions.push(subscription);

  const sessionId = `demo_cs_${state.nextSession++}`;
  const order = {
    dbId: state.nextOrderId++,
    sessionId,
    status: 'PAID',
    serialNumber,
    productId: product.dbId,
    subscriptionId: subscription.dbId,
    startDate,
  };
  state.orders.push(order);

  return { order, subscription, sessionId };
}

export function findPaidActiveSubscription(serialNumber) {
  return state.subscriptions.find((subscription) => {
    if (subscription.status !== 'ACTIVE' || subscription.serialNumber !== serialNumber) {
      return false;
    }
    const product = findProductByDbId(subscription.productId);
    return product && !product.distributorTrial;
  });
}

export function findOrderForSubscription(subscriptionId) {
  return state.orders.find((order) => order.subscriptionId === subscriptionId);
}

export function findOrderBySessionId(sessionId) {
  return state.orders.find((order) => order.sessionId === sessionId);
}

export function publicOrder(order) {
  const product = findProductByDbId(order.productId);
  const subscription = findSubscriptionByDbId(order.subscriptionId);
  return {
    id: encodeGraphqlId('OrderNode', order.dbId),
    status: order.status,
    device: { serialNumber: order.serialNumber },
    product: publicProduct(product),
    subscription: publicSubscription(subscription, state.products),
  };
}

export function addTrialSubscription(serialNumber) {
  const product = state.products.find((item) => item.distributorTrial);
  const subscription = createSubscriptionRecord({
    dbId: state.nextSubscriptionId++,
    product,
    serialNumber,
  });
  state.subscriptions.push(subscription);
  const receiptId = encodeGraphqlId('AcknowledgementReceiptNode', state.nextReceiptId++);
  return {
    acknowledgementReceipt: { id: receiptId },
    subscription: publicSubscription(subscription, state.products),
    error: null,
  };
}

export function checkoutPath(sessionId) {
  return `/payment-success?session_id=${sessionId}`;
}

export function isDemoSerial(serialNumber) {
  return serialNumber === DEMO_SERIAL;
}
